/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useEffect } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../store/rootReducer";
import { useAppDispatch } from "../../../store/rootStore";
import { ERequestStatus } from "../../../util/types";
import { selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import { selectStores, selectStoresStatus } from "../../stores/storage/stores.slice";
import CartSlice, { selectCurrentCart } from "../storage/cart.slice";
import { CartDeliveryMode } from "../storage/cart.types";

const CHRISTCHURCH_HOME_BRANCH_ID = "1221";

export function useApplyHomeBranchAsDefaultPickup(): void {
  const dispatch = useAppDispatch();
  const account = useSelector(selectCurrentFarmerAccount);
  const cart = useSelector(selectCurrentCart);
  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);
  const detailsStatus = useSelector((state: RootState) => state.auth.detailsStatus);

  useEffect(() => {
    const isCartAlreadyConfigured =
      cart && (cart.pickupStore || cart.cartDeliveryMode !== CartDeliveryMode.PICKUP);

    if (
      detailsStatus !== ERequestStatus.FULFILLED ||
      storesStatus !== "fulfilled" ||
      !account?.accountNumber ||
      isCartAlreadyConfigured
    ) {
      return;
    }

    // Fallback to Christchurch when the account has no home branch set in CE (work item 228338).
    const effectiveHomeBranchId = account.homeBranchId ?? CHRISTCHURCH_HOME_BRANCH_ID;

    const homePickupStore = stores.find(
      (s) =>
        s.isPickupLocation &&
        (s.inventorySiteId === effectiveHomeBranchId || s.orgUnitNumber === effectiveHomeBranchId)
    );

    if (!homePickupStore) {
      return;
    }

    dispatch(
      CartSlice.actions.setPickUpStore({
        accountNumber: account.accountNumber,
        pickupStore: homePickupStore,
      })
    );
    dispatch(
      CartSlice.actions.setDeliveryMode({
        accountNumber: account.accountNumber,
        cartDeliveryMode: CartDeliveryMode.PICKUP,
      })
    );
  }, [
    account?.accountNumber,
    account?.homeBranchId,
    cart,
    detailsStatus,
    dispatch,
    stores,
    storesStatus,
  ]);
}
